<kendo-dialog
  class="iqboost-v2-dialog"
  [title]="'iqboost.title' | translate"
  (close)="close()"
  width="90%"
  maxWidth="90%"
  height="90%"
  [style.z-index]="'.k-window, .ui-dialog' | maxZIndex: 99990"
>
  <div class="flex flex-col h-full min-h-0">
    <!-- Dialog Header -->
    <div
      class="flex-none flex items-center gap-2 h-[52px] px-4 bg-bush-green-5 border-0 border-b border-solid border-neutral-20"
    >
      <span class="maximizer-icons icon-iq-boost icon-20 text-bush-green-140" aria-hidden="true"></span>
      <!-- A household's name can be long: it is cut short here and shown whole on hover. -->
      <h6 class="m-0 min-w-0 truncate text-black" [attr.title]="scopeLabel() || null">
        {{ 'iqboost.title' | translate }}@if (scopeLabel(); as label) { - {{ label }}}
      </h6>

      <!-- Beta opt-in. It only appends an instruction to the prompt, so it takes effect on the
           next question asked rather than on anything already in the conversation. -->
      <div class="flex items-center gap-2 ml-auto mr-3">
        <kendo-switch
          size="small"
          [checked]="richUi()"
          (valueChange)="richUi.set($event)"
          [attr.aria-label]="'iqboost.chat.generativeUi' | translate"
        ></kendo-switch>
        <!-- The switch already carries this as its accessible name. -->
        <span aria-hidden="true" class="text-caption text-neutral-70 whitespace-nowrap">
          {{ 'iqboost.chat.generativeUi' | translate }}
        </span>
      </div>

      <button
        kendoButton
        fillMode="flat"
        class="h-8 w-8 rounded-md"
        [attr.aria-label]="'iqboost.chat.close' | translate"
        [title]="'iqboost.chat.close' | translate"
        (click)="close()"
      >
        <span class="fa-icons icon-solid icon-xmark icon-16" aria-hidden="true"></span>
      </button>
    </div>

    <div class="flex flex-1 min-h-0">
      <!-- Past conversations -->
      <aside
        class="flex flex-col flex-none w-[268px] min-h-0 border-0 border-r border-solid border-neutral-20"
      >
        <ai-iqboost-conversation-sidebar
          class="block flex-1 min-h-0 overflow-y-auto"
          [conversations]="conversations()"
          [activeThreadId]="threadId()"
          [busyThreadIds]="busyThreadIds()"
          [loading]="loadingConversations()"
          [disabled]="creatingThread()"
          (newQuestion)="startNewChat()"
          (conversationSelected)="openConversation($event)"
          (conversationRenamed)="renameConversation($event)"
          (conversationPinned)="pinConversation($event)"
          (conversationUnpinned)="unpinConversation($event)"
          (conversationDeleted)="requestDelete($event)"
        ></ai-iqboost-conversation-sidebar>

        <div class="flex-none px-4 py-3 border-0 border-t border-solid border-neutral-20">
          <ai-iqboost-security-status></ai-iqboost-security-status>
        </div>
      </aside>

      <!-- Chat -->
      <div class="flex flex-col flex-1 min-w-0 min-h-0">
        <div #scrollContainer class="flex-1 min-h-0 overflow-y-auto px-6 py-6">
          @if (loadingConversation()) {
            <span class="block text-center text-body-2 text-neutral-70 animate-pulse pt-14">
              {{ 'iqboost.chat.conversations.opening' | translate }}
            </span>
          } @else if (!hasConversation()) {
            <!-- Landing state: the question box sits under the greeting -->
            <div class="flex flex-col w-full max-w-[680px] mx-auto pt-14">
              <h5 class="m-0 text-center text-black">
                {{ 'iqboost.chat.heading' | translate }}
              </h5>
              <span class="block text-body-2 text-neutral-70 text-center mt-2 mb-5">
                {{ subtitle() }}
              </span>
              <ng-container *ngTemplateOutlet="askQuestion"></ng-container>
              <ai-iqboost-starter-prompts
                class="block mt-6"
                [prompts]="prompts()"
                [loading]="promptsLoading()"
                (promptSelected)="askPrompt($event)"
              ></ai-iqboost-starter-prompts>
            </div>
          } @else {
            <div class="flex flex-col gap-6 w-full max-w-[880px] mx-auto">
              @for (chat of chatHistory(); track $index) {
                @if (chat.from === IQChatFrom.User) {
                  <!-- The reader's question: the only turn on the right, in a green bubble with
                       the files it was sent with under it -->
                  <div class="flex flex-col items-end gap-2">
                    <ai-answer-content
                      class="block max-w-[75%] min-w-0 px-4 py-2.5 rounded-lg bg-bush-green-10 text-body-2 text-black break-words"
                      [content]="chat.content"
                      [fromReader]="true"
                    ></ai-answer-content>
                    @if (chat.attachments; as attachments) {
                      <!-- Chips that wrap line up to the right too, like the bubble. -->
                      <ai-iqboost-attachment-chips
                        class="block max-w-[75%] [&_kendo-chiplist]:justify-end"
                        [files]="attachments"
                        [removable]="false"
                      ></ai-iqboost-attachment-chips>
                    }
                  </div>
                } @else {
                  <div class="flex items-start gap-3">
                    <ng-container *ngTemplateOutlet="iqBoostAvatar"></ng-container>
                    <div class="flex flex-col gap-3 min-w-0 w-full">
                      @if (chat.steps; as steps) {
                        <ai-iqboost-answer-steps [steps]="steps"></ai-iqboost-answer-steps>
                      }
                      @for (confirmation of chat.confirmations ?? []; track confirmation.id) {
                        <ai-iqboost-write-confirmation
                          [confirmation]="confirmation"
                          [busy]="loading() || confirmation.id !== waitingOn()?.id"
                          (decided)="decide(confirmation, $event)"
                        ></ai-iqboost-write-confirmation>
                      }
                      <!-- 14px body copy: the size the markdown renderer's headings and tables are
                           made to sit in. -->
                      <ai-answer-content
                        class="block text-body-2 text-black min-w-0 w-full"
                        [content]="chat.content"
                        [citations]="true"
                        [actions]="true"
                        [followUps]="$last"
                        [threadId]="threadId()"
                        (followUpAsked)="askFollowUp($event)"
                      ></ai-answer-content>
                      @if (chat.addon; as addon) {
                        @for (view of addonViews(addon); track view) {
                          <ng-container [maximizerIqboostOverrideView]="view"></ng-container>
                        }
                      }
                      @for (view of handoffViews(chat.handoffs ?? []); track view) {
                        <ng-container [maximizerIqboostOverrideView]="view"></ng-container>
                      }
                      <div class="flex justify-end -mt-1">
                        <ai-iqboost-answer-feedback
                          [value]="chat.feedback"
                          (rated)="rateAnswer($index, $event)"
                        ></ai-iqboost-answer-feedback>
                      </div>
                    </div>
                  </div>
                }
              }

              @if (loading()) {
                <!-- The run in flight: the steps done so far, then what it is doing or the answer
                     as it streams in -->
                <div class="flex items-start gap-3">
                  <ng-container *ngTemplateOutlet="iqBoostAvatar"></ng-container>
                  <div class="flex flex-col gap-3 min-w-0 w-full">
                    @if (runSteps().length > 0 || runningSteps().length > 0 || thinking()) {
                      <ai-iqboost-answer-steps
                        [steps]="runSteps()"
                        [running]="runningSteps()"
                        [thinking]="thinking()"
                      ></ai-iqboost-answer-steps>
                    }
                    @if (streamingContent(); as streaming) {
                      <ai-answer-content
                        class="block text-body-2 text-black min-w-0 w-full"
                        [content]="streaming"
                        [streaming]="true"
                      ></ai-answer-content>
                    } @else if (reasoning(); as reasoningText) {
                      <!-- What the model wrote before calling a tool -->
                      <ai-answer-content
                        class="block text-body-2 text-neutral-70 min-w-0 w-full"
                        [content]="reasoningText"
                        [streaming]="true"
                      ></ai-answer-content>
                    }
                    @for (view of handoffViews(runHandoffs()); track view) {
                      <ng-container [maximizerIqboostOverrideView]="view"></ng-container>
                    }
                    @for (confirmation of runConfirmations(); track confirmation.id) {
                      <ai-iqboost-write-confirmation
                        [confirmation]="confirmation"
                        [busy]="true"
                      ></ai-iqboost-write-confirmation>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>

        <!-- Once the conversation started, the question box moves to the bottom -->
        @if (hasConversation() && !loadingConversation()) {
          <div class="flex-none px-6 pt-2">
            <div class="w-full max-w-[880px] mx-auto">
              <ng-container *ngTemplateOutlet="askQuestion"></ng-container>
            </div>
          </div>
        }

        <div class="flex-none w-full px-3 pb-2 flex flex-col items-center justify-center">
          <span class="text-xs text-neutral-80 text-center mt-2">
            {{ 'iqboost.chat.adviceDisclaimer' | translate }}
          </span>
          <span
            class="text-xs text-neutral-80 text-center"
            [innerHTML]="'iqboost.summary.accuracyDisclaimer' | translate"
          ></span>
        </div>
      </div>
    </div>
  </div>

  <ng-template #iqBoostAvatar>
    <span
      class="flex items-center justify-center shrink-0 w-8 h-8 rounded-full bg-bush-green-10"
      aria-hidden="true"
    >
      <span class="maximizer-icons icon-iq-boost icon-16 text-bush-green-140"></span>
    </span>
  </ng-template>

  <ng-template #askQuestion>
    <div
      class="flex flex-col bg-white border border-solid border-neutral-30 rounded focus-within:border-bush-green-100"
    >
      <!-- The wrapper draws the box around the textarea and its buttons. -->
      <kendo-textarea
        #txtQuestion
        class="block font-normal !border-0 !shadow-none"
        fillMode="flat"
        resizable="none"
        [rows]="hasConversation() ? 2 : 4"
        [placeholder]="
          awaitingConfirmation()
            ? ('iqboost.chat.confirmations.waiting' | translate)
            : placeholder()
        "
        [disabled]="awaitingConfirmation()"
        [attr.aria-label]="'iqboost.chat.askQuestions' | translate"
        (keydown.enter)="submitQuestion(txtQuestion, $event)"
      ></kendo-textarea>

      <div class="flex items-center justify-between px-2 pb-2">
        <!-- PDFs only; the input stays hidden and is opened by the Attach button. -->
        <input
          #fileInput
          type="file"
          class="hidden"
          accept="application/pdf,.pdf"
          multiple
          aria-label="{{ 'iqboost.chat.attachFile' | translate }}"
          (change)="selectFiles(fileInput)"
        />
        <button
          kendoButton
          fillMode="outline"
          rounded="full"
          size="small"
          class="h-8"
          [title]="'iqboost.chat.attachFile' | translate"
          (click)="fileInput.click()"
        >
          <span class="flex items-center gap-1.5">
            <span class="fa-icons icon-solid icon-14 icon-paperclip" aria-hidden="true"></span>
            {{ 'iqboost.chat.attach' | translate }}
          </span>
        </button>

        <button
          kendoButton
          themeColor="primary"
          class="h-8 rounded-md"
          [disabled]="loading() || loadingConversation() || awaitingConfirmation()"
          (click)="submitQuestion(txtQuestion)"
        >
          <span class="flex items-center gap-2">
            <span class="fa-icons icon-solid icon-16 icon-paper-plane" aria-hidden="true"></span>
            {{ 'iqboost.chat.ask' | translate }}
          </span>
        </button>
      </div>
    </div>

    <ai-iqboost-attachment-chips
      class="block mt-2 empty:hidden"
      [files]="attachments()"
      (removed)="removeAttachment($event)"
    ></ai-iqboost-attachment-chips>
  </ng-template>
</kendo-dialog>

@if (pendingDelete(); as pending) {
  <kendo-dialog
    [title]="'iqboost.chat.conversations.deleteConfirm.title' | translate"
    [width]="420"
    [maxWidth]="'90%'"
    [style.z-index]="'.k-window, .ui-dialog' | maxZIndex: 99990"
    (close)="cancelDelete()"
  >
    <span class="block text-body-2 text-black break-words">
      {{
        'iqboost.chat.conversations.deleteConfirm.message'
          | translate
            : {
                title:
                  pending.title || ('iqboost.chat.conversations.untitled' | translate),
              }
      }}
    </span>

    <kendo-dialog-actions layout="end">
      <button kendoButton fillMode="outline" (click)="cancelDelete()">
        {{ 'iqboost.buttons.cancel' | translate }}
      </button>
      <button kendoButton themeColor="primary" (click)="confirmDelete()">
        {{ 'iqboost.chat.conversations.deleteConfirm.confirm' | translate }}
      </button>
    </kendo-dialog-actions>
  </kendo-dialog>
}

@if (error(); as shownError) {
  <kendo-dialog
    [width]="460"
    [minHeight]="175"
    [style.z-index]="'.k-window, .ui-dialog' | maxZIndex: 99990"
  >
    <div class="px-5">
      <h6 class="py-4 flex text-black">
        {{ shownError.title | translate }}
      </h6>

      <div class="flex">
        <img
          class="w-[62px] mr-4"
          [src]="cdn + '/assets/images/ai-error-message.svg'"
          alt=""
        />
        <span class="text-body-2 text-black whitespace-pre-line break-words min-w-0">
          {{ shownError.message | translate: shownError.params }}
        </span>
      </div>

      <div class="flex justify-end my-5">
        <button
          kendoButton
          class="uppercase"
          themeColor="primary"
          (click)="error.set(null)"
        >
          {{ 'iqboost.buttons.ok' | translate }}
        </button>
      </div>
    </div>
  </kendo-dialog>
}
